import React, { useState, useCallback } from 'react';
import { Upload, X, ImagePlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axiosInstance from '@/utils/axios';

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const ImageUploader = ({ images, onChange, maxImages = MAX_IMAGES }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = useCallback(
    async (files) => {
      const fileArray = Array.from(files);

      // Check max images
      if (images.length + fileArray.length > maxImages) {
        toast.error(`Máximo ${maxImages} imágenes permitidas`);
        return;
      }

      // Validate files
      const validFiles = [];
      for (const file of fileArray) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          toast.error(
            `${file.name}: Formato no permitido. Use JPG, PNG, WebP o GIF`
          );
          continue;
        }
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`${file.name}: El archivo excede 10MB`);
          continue;
        }
        validFiles.push(file);
      }

      if (validFiles.length === 0) return;

      setUploading(true);

      try {
        const formData = new FormData();
        validFiles.forEach((file) => {
          formData.append('files', file);
        });

        const { data } = await axiosInstance.post('/upload/images', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (data.uploaded && data.uploaded.length > 0) {
          const newImages = data.uploaded.map((img) => img.url);
          onChange([...images, ...newImages]);
          toast.success(
            `${data.uploaded.length} imagen(es) subida(s) correctamente`
          );
        }

        if (data.errors && data.errors.length > 0) {
          data.errors.forEach((err) => {
            toast.error(`${err.file}: ${err.error}`);
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Error al subir las imágenes');
      } finally {
        setUploading(false);
      }
    },
    [images, onChange, maxImages]
  );

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleInputChange = useCallback(
    (e) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles]
  );

  const removeImage = useCallback(
    (index) => {
      const newImages = images.filter((_, i) => i !== index);
      onChange(newImages);
    },
    [images, onChange]
  );

  return (
    <div className='space-y-4'>
      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4'>
          {images.map((url, index) => (
            <div
              key={index}
              className='relative group aspect-square rounded-lg overflow-hidden border-2 border-slate-200'
            >
              <img
                src={url}
                alt={`Imagen ${index + 1}`}
                className='w-full h-full object-cover'
              />
              <button
                type='button'
                onClick={() => removeImage(index)}
                className='absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600'
                data-testid={`remove-image-${index}`}
              >
                <X className='w-4 h-4' />
              </button>
              <div className='absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded'>
                {index + 1}/{maxImages}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {images.length < maxImages && (
        <div
          className={`
            relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200
            ${
              dragActive
                ? 'border-primary bg-primary/5'
                : 'border-slate-300 hover:border-primary/50 hover:bg-slate-50'
            }
            ${uploading ? 'pointer-events-none opacity-60' : 'cursor-pointer'}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() =>
            !uploading && document.getElementById('image-upload').click()
          }
          data-testid='image-upload-area'
        >
          <input
            id='image-upload'
            type='file'
            multiple
            accept={ALLOWED_TYPES.join(',')}
            onChange={handleInputChange}
            className='hidden'
            disabled={uploading}
          />

          {uploading ? (
            <div className='flex flex-col items-center'>
              <Loader2 className='w-12 h-12 text-primary animate-spin mb-4' />
              <p className='text-slate-600 font-medium'>Subiendo imágenes...</p>
            </div>
          ) : (
            <div className='flex flex-col items-center'>
              <div className='w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4'>
                <ImagePlus className='w-8 h-8 text-primary' />
              </div>
              <p className='text-slate-700 font-medium mb-2'>
                Arrastra tus imágenes aquí
              </p>
              <p className='text-slate-500 text-sm mb-4'>
                o haz clic para seleccionar
              </p>
              <div className='flex flex-wrap justify-center gap-2 text-xs text-slate-400'>
                <span className='px-2 py-1 bg-slate-100 rounded'>JPG</span>
                <span className='px-2 py-1 bg-slate-100 rounded'>PNG</span>
                <span className='px-2 py-1 bg-slate-100 rounded'>WebP</span>
                <span className='px-2 py-1 bg-slate-100 rounded'>GIF</span>
                <span className='px-2 py-1 bg-slate-100 rounded'>Máx 10MB</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status */}
      <div className='flex items-center justify-between text-sm text-slate-500'>
        <span>
          {images.length} de {maxImages} imágenes
        </span>
        {images.length > 0 && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => onChange([])}
            className='text-red-500 hover:text-red-600 hover:bg-red-50'
          >
            Eliminar todas
          </Button>
        )}
      </div>
    </div>
  );
};

export default ImageUploader;
